import React, { useState } from "react";
import {
  Box,
  Paper,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
} from "@mui/material";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import GridOnOutlinedIcon from "@mui/icons-material/GridOnOutlined";
import SectionHeader from "../components/common/SectionHeader";
import EmployeesTab from "../components/employees/EmployeesTab";
import LocationsTab from "../components/locations/LocationsTab";
import UserRolesTab from "../components/access-control/UserRolesTab";
import RolesCatalogTab from "../components/access-control/RolesCatalogTab";
import ModulesCatalogTab from "../components/access-control/ModulesCatalogTab";
import PermissionsCatalogTab from "../components/access-control/PermissionsCatalogTab";
import RolePermissionsMatrixTab from "../components/access-control/RolePermissionsMatrixTab";

const PAGES = {
  EMPLOYEES: "employees",
  LOCATIONS: "locations",
  USER_ROLES: "userRoles",
  ROLES: "roles",
  MODULES: "modules",
  PERMISSIONS: "permissions",
  ROLE_PERMISSIONS: "rolePermissions",
};

const MENU_CONFIG = [
  {
    sectionKey: "people",
    label: "People",
    description: "Employees & locations",
    icon: GroupsOutlinedIcon,
    items: [
      { key: PAGES.EMPLOYEES, label: "Employees", icon: BadgeOutlinedIcon },
      { key: PAGES.LOCATIONS, label: "Locations", icon: LocationOnOutlinedIcon },
    ],
  },
  {
    sectionKey: "accessControl",
    label: "Access Control",
    description: "Roles, modules and permissions",
    icon: KeyOutlinedIcon,
    items: [
      { key: PAGES.USER_ROLES, label: "User Roles", icon: PersonOutlinedIcon },
      { key: PAGES.ROLES, label: "Roles", icon: SecurityOutlinedIcon },
      { key: PAGES.MODULES, label: "Modules", icon: ViewModuleOutlinedIcon },
      {
        key: PAGES.PERMISSIONS,
        label: "Permissions",
        icon: VpnKeyOutlinedIcon,
      },
      {
        key: PAGES.ROLE_PERMISSIONS,
        label: "Role Access",
        icon: GridOnOutlinedIcon,
      },
    ],
  },
];

const SIDEBAR_WIDTH = 240;

const AdministrationPage = () => {
  const [activePage, setActivePage] = useState(PAGES.EMPLOYEES);

  return (
    <Box sx={{ p: 2 }}>
      <SectionHeader
        icon={AdminPanelSettingsIcon}
        title="Administration"
        description="Manage employees, locations, user accounts, and role assignments."
        iconColor="#ef5350"
      />

      <Box sx={{ display: "flex", gap: 2 }}>
        {/* Sidebar navigation */}
        <Paper
          sx={{
            width: SIDEBAR_WIDTH,
            minWidth: SIDEBAR_WIDTH,
            flexShrink: 0,
            alignSelf: "flex-start",
            position: "sticky",
            top: 80,
          }}
        >
          {MENU_CONFIG.map((section, idx) => (
            <Box key={section.sectionKey}>
              {idx > 0 && <Divider />}
              <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <section.icon
                    sx={{ fontSize: 18, color: "text.secondary" }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.secondary"
                    sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
                  >
                    {section.label}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  color="text.disabled"
                  sx={{ fontSize: 11 }}
                >
                  {section.description}
                </Typography>
              </Box>
              <List dense disablePadding sx={{ pb: 0.5 }}>
                {section.items.map((item) => (
                  <ListItemButton
                    key={item.key}
                    selected={activePage === item.key}
                    onClick={() => setActivePage(item.key)}
                    sx={{
                      mx: 1,
                      borderRadius: 1,
                      mb: 0.25,
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        "&:hover": { bgcolor: "primary.dark" },
                        "& .MuiListItemIcon-root": {
                          color: "primary.contrastText",
                        },
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <item.icon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        variant: "body2",
                        fontWeight: 500,
                      }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          ))}
        </Paper>

        {/* Content area */}
        <Paper sx={{ flex: 1, p: 2, minHeight: 400 }}>
          {activePage === PAGES.EMPLOYEES && <EmployeesTab />}
          {activePage === PAGES.LOCATIONS && <LocationsTab />}
          {activePage === PAGES.USER_ROLES && <UserRolesTab />}
          {activePage === PAGES.ROLES && <RolesCatalogTab />}
          {activePage === PAGES.MODULES && <ModulesCatalogTab />}
          {activePage === PAGES.PERMISSIONS && <PermissionsCatalogTab />}
          {activePage === PAGES.ROLE_PERMISSIONS && (
            <RolePermissionsMatrixTab />
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default AdministrationPage;
